import type { RefObject } from 'react';
import { useEffect } from 'react';

// Also written as a literal in `NativeShareMobile`'s arbitrary-value `top` class: Tailwind only generates classes
// it can read as whole strings, so that class cannot use this constant.
const BANNER_HEIGHT_CSS_VARIABLE = '--spiking-petition-banner-height';

/**
 * Publishes the banner's rendered height on `<html>` so `NativeShareMobile`, which sticks to the same
 * viewport edge in a different part of the tree, can sit below the banner instead of under it. Removed on
 * unmount, so a dismissed banner leaves no offset.
 */
export const useBannerHeightCssVariable = (bannerRef: RefObject<HTMLElement | null>): void => {
	useEffect(() => {
		const banner = bannerRef.current;
		if (!banner) return undefined;

		const root = document.documentElement;
		const publish = () =>
			root.style.setProperty(BANNER_HEIGHT_CSS_VARIABLE, `${Math.round(banner.getBoundingClientRect().height)}px`);
		publish();

		// The height changes with the viewport width (the text clamps to 2 lines on mobile) and during the exit animation.
		const observer = new ResizeObserver(publish);
		observer.observe(banner);

		return () => {
			observer.disconnect();
			root.style.removeProperty(BANNER_HEIGHT_CSS_VARIABLE);
		};
	}, [bannerRef]);
};
